import { useCallback, useMemo } from 'react';
import type { ButtonProperties } from 'ui';
import { type ProgramEventData, useEventActions, useGetDialInHrefSocial, useRefreshingJoinPeriod } from '../../hooks';
import type { EventActionsView } from '../../types';
import { getLastChangeRequestStatus, isAfterJoinPeriod, isAllowedChangeRequestStatus, isJoinPeriod } from '../../utils';
import { EventActionsButtons } from '../event-actions-buttons';

type P = {
  authorId?: string;
  event: ProgramEventData;
  hasLabel?: boolean;
  type?: ButtonProperties['type'];
  view?: EventActionsView;
};

const EventActionsSocial = ({ authorId, event, hasLabel, type, view = 'list' }: P) => {
  const { dropdownOptions, isAccepting, isJoining, isRejecting, onEventActionClick, onRequestEnterConference } =
    useEventActions({ data: { ...event, authorId } });
  const { changeRequests, currentUserRsvp, dialInInformation, endTime } = event;
  const requestStatus = getLastChangeRequestStatus(changeRequests);

  // TODO: fix when bmgId is null
  const isJoinPeriodRefreshing = useRefreshingJoinPeriod(useCallback(() => isJoinPeriod(event), [event]));

  const dialInData = useGetDialInHrefSocial({ dialInInformation });

  const dropdownButtonProperties = useMemo(
    () => ({
      dropdownOptions,
      isAccepting,
      isRejecting,
      type,
    }),
    [dropdownOptions, isAccepting, isRejecting, type],
  );

  const joinButtonProperties = useMemo(
    () => ({
      dialInData,
      isJoining,
      onEnter() {
        onRequestEnterConference(event);
      },
    }),
    [isJoining, onRequestEnterConference, event, dialInData],
  );

  const rsvpButtonsProperties = useMemo(
    () => ({
      actionGoing() {
        onEventActionClick('going');
      },
      actionNotGoing() {
        onEventActionClick('not_going');
      },
      hasLabel,
      isAccepting,
      isRejecting,
    }),
    [onEventActionClick, hasLabel, isAccepting, isRejecting],
  );

  if (!isAllowedChangeRequestStatus({ status: requestStatus })) {
    return null;
  }

  return (
    <EventActionsButtons
      currentUserRsvp={currentUserRsvp}
      dropdownButton={dropdownButtonProperties}
      isAfterJoinPeriod={isAfterJoinPeriod(endTime)}
      isJoinPeriod={isJoinPeriodRefreshing}
      joinButton={joinButtonProperties}
      rsvpButtons={rsvpButtonsProperties}
      view={view}
    />
  );
};

export default EventActionsSocial;
